import type { Campaign } from '../../types/campaign';
import type { Account, AccountType } from '../../types/account';

// Pre-P2b-3 polish round 2 (Item 2): pure helpers for the CampaignList
// filter bar. Extracted from the component so the rules are unit-testable
// without mounting React. Same data semantics either way.

// Designation filter (single-select). 'regular' = no flag set.
export type DesignationFilter = 'all' | 'alternate' | 'opt_out' | 'regular';

// Assigned scope filter. 'multi' = shareTargets span ≥ 2 distinct
// account-type levels. The single-level scopes require EVERY share
// target to be of that type — a Campaign shared with brand A AND
// brand B counts as 'brand'; a Campaign shared with brand A AND group
// X counts as 'multi'. 'other' is a long-tail catch (head_office /
// region / dealership) so non-fitness brands aren't dropped.
export type ScopeFilter = 'all' | 'brand' | 'group' | 'location' | 'multi' | 'other';

const OTHER_TYPES = new Set<AccountType>(['head_office', 'region', 'dealership']);

export function matchesScope(
  campaign: Pick<Campaign, 'shareTargets'>,
  byId: Map<string, Account>,
  scope: ScopeFilter,
): boolean {
  if (scope === 'all') return true;
  const targets = campaign.shareTargets ?? [];
  if (targets.length === 0) return false;
  const types = new Set<AccountType>();
  for (const t of targets) {
    const acc = byId.get(t.accountId);
    if (acc) types.add(acc.accountType);
  }
  if (types.size === 0) return false;
  if (scope === 'multi') return types.size >= 2;
  if (scope === 'brand') return types.size === 1 && types.has('brand');
  if (scope === 'group') return types.size === 1 && types.has('group');
  if (scope === 'location') return types.size === 1 && types.has('location');
  if (scope === 'other') {
    if (types.size !== 1) return false;
    const [only] = types;
    return only !== undefined && OTHER_TYPES.has(only);
  }
  return false;
}

// Designation match. Pulled out so the test file can pin the four
// branches against a single Campaign shape rather than re-deriving
// `c.isAlternate` / `c.isOptOut` everywhere.
export function matchesDesignation(
  campaign: Pick<Campaign, 'isAlternate' | 'isOptOut'>,
  filter: DesignationFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'alternate') return campaign.isAlternate;
  if (filter === 'opt_out') return campaign.isOptOut;
  // 'regular' — neither flag set.
  return !campaign.isAlternate && !campaign.isOptOut;
}
